import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, DropdownMenu, Spinner, Modal, useStateContext } from '@veda/dashboard-ui';
import { MdInbox, MdAdd, MdEdit, MdDelete, MdVisibility } from 'react-icons/md';
import { usePropostaApi } from '../services/propostaApi';
import { usePppAiContext } from '../../ppp/hooks/usePppAiContext';

const STATO_BADGE = {
  bozza:                         { label: 'Bozza',                         cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  ricevuta:                      { label: 'Presentata',                    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  in_valutazione:                { label: 'In valutazione',                cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  interesse_pubblico_dichiarato: { label: 'Interesse pubblico dichiarato', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  ammessa:                       { label: 'Ammessa',                       cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  respinta:                      { label: 'Respinta',                      cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  ritirata:                      { label: 'Ritirata',                      cls: 'bg-gray-100 text-gray-500' },
};

function BadgeStato({ stato }) {
  const b = STATO_BADGE[stato] ?? { label: stato, cls: 'bg-blue-100 text-blue-700' };
  return <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${b.cls}`}>{b.label}</span>;
}

export default function ProposteListPage() {
  usePppAiContext('ppp.proposte');
  const api = usePropostaApi();
  const navigate = useNavigate();

  const { addModal } = useStateContext();
  const [proposte, setProposte] = useState([]);
  const [loading, setLoading]   = useState(true);

  const carica = useCallback(() => {
    setLoading(true);
    api.listProposte({
      onSuccess: (data) => { setProposte(Array.isArray(data) ? data : []); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const elimina = (id) => {
    addModal(
      <Modal
        title="Elimina bozza"
        content="Sei sicuro di voler eliminare questa bozza? L'operazione non può essere annullata."
        confirm="Elimina"
        style="danger"
        onClick={() => api.eliminaProposta(id, { onSuccess: carica, onFormError: () => {} })}
      />
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-64"><Spinner /></div>;
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
            <MdInbox size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Le mie proposte</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Proposte PPP presentate dalla tua azienda (art. 193)</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/operatore/proposte/nuova')}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
        >
          <MdAdd /> Nuova proposta
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <Table
          cols={[
            { name: 'titolo',           label: 'Titolo' },
            { name: 'procedimentoTitolo', label: 'Procedimento' },
            { name: 'numeroProtocollo', label: 'Protocollo', size: 'w-40' },
            { name: 'stato',            label: 'Stato',      size: 'w-32' },
            { name: 'azioni',           label: '',           size: 'w-10', orderable: 0 },
          ]}
          cells_style={{
            numeroProtocollo: (row) => row.numeroProtocollo ?? <span className="text-gray-400">—</span>,
            stato:  (row) => <BadgeStato stato={row.stato} />,
            azioni: (row) => (
              <DropdownMenu
                menu_items={[
                  <button key="vedi"
                    onClick={() => navigate(row.stato === 'bozza'
                      ? `/operatore/proposte/${row.id}/modifica`
                      : `/operatore/proposte/${row.id}`)}
                    className="w-full flex items-center gap-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600">
                    {row.stato === 'bozza' ? <><MdEdit /> Continua compilazione</> : <><MdVisibility /> Vedi avanzamento</>}
                  </button>,
                  row.stato === 'bozza' && (
                    <button key="del" onClick={() => elimina(row.id)}
                      className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                      <MdDelete /> Elimina bozza
                    </button>
                  ),
                ].filter(Boolean)}
              />
            ),
          }}
          dataset={proposte}
        />
        {proposte.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <MdInbox className="mx-auto text-5xl mb-3" />
            <p>Non hai ancora presentato proposte. Clicca su <strong>Nuova proposta</strong> per iniziare.</p>
          </div>
        )}
      </div>

    </div>
  );
}
